import { useMediaQuery } from '@mui/material';
import { isToday } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../constants';
import { LocalStorageKey } from '../../constants/localStorageKey';
import {
  CustomerBaseValueViewResponse,
  CustomerExperienceViewResponse,
  SalesEfficiencyViewResponse,
  useAsyncAction,
  useLocalStorage,
  useStoreInformation,
  useTransaction,
  useUser,
} from '../../hooks';
import { useCurrencyTools } from '../../hooks/useCurrencyTools';
import { usePageEventTracker } from '../../hooks/usePageEventTracker';
import { Icon, Icons, Typography } from '../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import {
  Animation,
  Animations,
  Button,
  DropdownMenu,
  DynamicTab,
  FilterDrawer,
  FilterTag,
  FilterTagsWidget,
  FilterType,
} from '../../stories/molecules';
import { Page } from '../../stories/organisms';
import { muiTheme, Typographies } from '../../theme';
import { commonTranslations, homeTranslations } from '../../translations';
import { areaResultTranslations } from '../../translations/areaResult';
import { filterTranslations } from '../../translations/filter';
import { salesAnalysisTranslation } from '../../translations/salesAnalysis';
import { getDatePickerLabel, isFailureResponse } from '../../utils';
import { HomeDatePicker } from '../home/HomeDatePicker';
import { BreakdownView } from './breakdownView/BreakdownView';
import { CustomerBaseValueView } from './customerBaseView/CustomerBaseValueView';
import { CustomerExperienceView } from './customerExperienceView/CustomerExperienceView';
import { SalesEfficiencyView } from './salesEfficiencyView/SalesEfficiencyView';

export const AreaResultPage: FC<unknown> = observer(() => {
  usePageEventTracker('area_result');

  const { t } = useTranslation();
  const { widgetCategory, datePicker } = homeTranslations;
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const { fetchCustomerBaseValueViewData, fetchSalesEfficiencyViewData, fetchCustomerExperienceViewData } =
    useTransaction();
  const { getItem, setItem } = useLocalStorage();

  const {
    get: { storesDetails },
  } = useUser();
  const { areaName } = useStoreInformation();

  const filteredStoresByArea = storesDetails.filter((store) => store.area === areaName);
  const openStores = filteredStoresByArea.filter((store) => store.status === 'OPEN');
  const storesByArea = openStores.map((store) => store.storeId);

  const [isBreakdownViewVisible, setIsBreakdownViewVisible] = useState(false);
  // const showBreakdownView = () => setIsBreakdownViewVisible(true);
  const hideBreakdownView = () => setIsBreakdownViewVisible(false);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentInterval, setCurrentInterval] = useState<StorePerformanceInterval>(StorePerformanceInterval.DAILY);

  const [tabIndex, setTabIndex] = useState(Number(getItem(LocalStorageKey.SelectedAreaResultTab)) || 0);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { currencyCode: targetCurrency } = useStoreInformation();

  const { getFormattedCurrency } = useCurrencyTools(currentInterval, selectedDate);

  const [dateLabel, setDateLabel] = useState(
    getDatePickerLabel(
      selectedDate,
      currentInterval,
      t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue),
    ),
  );

  const {
    result: customerBaseValueViewData,
    isLoading: customerBaseValueViewLoading,
    isError: customerBaseValueViewError,
    triggerAction: triggerFetchCustomerBaseValueView,
    reset: resetCustomerBaseValueView,
  } = useAsyncAction<CustomerBaseValueViewResponse>();

  const {
    result: salesEfficiencyViewData,
    isLoading: salesEfficiencyViewLoading,
    isError: salesEfficiencyViewError,
    triggerAction: triggerFetchSalesEfficiencyView,
    reset: resetSalesEfficiencyView,
  } = useAsyncAction<SalesEfficiencyViewResponse>();

  const {
    result: customerExperienceViewData,
    isLoading: customerExperienceViewLoading,
    isError: customerExperienceViewError,
    triggerAction: triggerFetchCustomerExperienceView,
    reset: resetCustomerExperienceView,
  } = useAsyncAction<CustomerExperienceViewResponse>();

  const fetchCustomerData = async () => {
    const request = createStorePerformanceRequest({
      interval: currentInterval,
      currentDate: selectedDate,
    });
    const response = await fetchCustomerBaseValueViewData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }
    return response.data;
  };

  const fetchSalesData = async () => {
    const request = createStorePerformanceRequest({
      interval: currentInterval,
      currentDate: selectedDate,
    });
    const response = await fetchSalesEfficiencyViewData(request, targetCurrency);

    if (isFailureResponse(response)) {
      throw new Error();
    }
    return response.data;
  };

  const fetchCustomerExperienceData = async () => {
    const request = createStorePerformanceRequest({
      interval: currentInterval,
      currentDate: selectedDate,
    });
    const response = await fetchCustomerExperienceViewData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }
    return response.data;
  };

  useEffect(() => {
    if (tabIndex === 0 && !salesEfficiencyViewLoading && salesEfficiencyViewData === null) {
      triggerFetchSalesEfficiencyView(fetchSalesData);
    }
    if (tabIndex === 1 && !customerBaseValueViewLoading && customerBaseValueViewData === null) {
      triggerFetchCustomerBaseValueView(fetchCustomerData);
    }

    if (tabIndex === 2 && !customerExperienceViewLoading && customerExperienceViewData === null) {
      triggerFetchCustomerExperienceView(fetchCustomerExperienceData);
    }
  }, [tabIndex]);

  useEffect(() => {
    setSelectedStores([]);
    resetSalesEfficiencyView();
    resetCustomerBaseValueView();
    resetCustomerExperienceView();

    if (tabIndex === 0) {
      triggerFetchSalesEfficiencyView(fetchSalesData);
    }
    if (tabIndex === 1) {
      triggerFetchCustomerBaseValueView(fetchCustomerData);
    }
    if (tabIndex === 2) {
      triggerFetchCustomerExperienceView(fetchCustomerExperienceData);
    }
  }, [selectedDate]);

  const handleTabChange = (event: React.SyntheticEvent, newTabIndex: number) => {
    setItem(LocalStorageKey.SelectedAreaResultTab, newTabIndex.toString());
    setTabIndex(newTabIndex);
  };

  const handleDateChange = (date: Date, interval: StorePerformanceInterval) => {
    setSelectedDate(date);
    setCurrentInterval(interval);
    setDateLabel(
      getDatePickerLabel(date, interval, t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue)),
    );
  };

  const handleStoreTagClick = (store: string) => {
    const keyIndex = selectedStores.indexOf(store);

    const updatedSelectedStores = [...selectedStores];
    updatedSelectedStores.splice(keyIndex, 1);
    setSelectedStores(updatedSelectedStores);
  };

  const handleDateFilterTagClick = () => {
    handleDateChange(new Date(), StorePerformanceInterval.DAILY);
  };

  const clearAllFilters = () => {
    if (!isToday(selectedDate)) {
      handleDateChange(new Date(), StorePerformanceInterval.DAILY);
    }

    setSelectedStores([]);
  };

  const storeMenuItems = storesByArea.map((item) => {
    return {
      key: item,
      item: <Typography margin={[0, 2, 0]}>{item}</Typography>,
    };
  });

  const isDateRangeFilterActive = !isToday(selectedDate);
  const isStoreFilterActive = selectedStores.length > 0;

  const isAnyFilterActive = isDateRangeFilterActive || isStoreFilterActive;

  return (
    <Page
      header={
        isBreakdownViewVisible ? undefined : (
          <RowFlexContainer alignItems="center" gap={[5]} padding={isTablet ? [0, 4] : [0, 8]} height={[12]}>
            <HomeDatePicker
              currentDate={selectedDate}
              dateLabel={dateLabel}
              onDateChange={handleDateChange}
              showMonthyPicker
              isDatePickerDisabled={
                customerBaseValueViewLoading || customerExperienceViewLoading || salesEfficiencyViewLoading
              }
            />

            {!isMobile && (
              <DropdownMenu
                dropdownLabel="Store"
                menuPosition="left"
                maxHeight={'50vh'}
                menuItems={storeMenuItems}
                checkbox={true}
                multiSelect
                initialSelected={selectedStores}
                buttonContentProps={{
                  isDisabled:
                    customerBaseValueViewLoading ||
                    customerExperienceViewLoading ||
                    salesEfficiencyViewLoading ||
                    customerBaseValueViewError ||
                    salesEfficiencyViewError ||
                    customerExperienceViewError,
                }}
                onSelect={(selectedItemKeys) => {
                  setSelectedStores(selectedItemKeys);
                }}
              />
            )}

            {isMobile && (
              <Button
                data-testid="all-filters-button"
                label={t(commonTranslations.allFilters.key, commonTranslations.allFilters.defaultValue)}
                buttonContentProps={{
                  labelPosition: 'left',
                  iconOptions: {
                    icon: Icons.FILTER,
                    size: 'small',
                  },
                  textOptions: {
                    whiteSpace: 'nowrap',
                  },
                }}
                aria-haspopup="true"
                aria-expanded={isFilterDrawerOpen ? 'true' : undefined}
                onClick={() => setIsFilterDrawerOpen(true)}
                variant="text"
              />
            )}

            {/* <RowFlexContainer flex={1} justifyContent="end"> Hiding this for card: BTCOBUI-3604
              <Button
                data-testid="show-breakdown-button"
                label={!isMobile ? 'Show Breakdown' : ''}
                buttonContentProps={{
                  iconOptions: {
                    icon: Icons.CLOCK,
                    size: 'small',
                  },
                  textOptions: {
                    whiteSpace: 'nowrap',
                  },
                }}
                onClick={showBreakdownView}
                variant="text"
                minWidth={[0]}
                disabled={currentInterval != StorePerformanceInterval.DAILY}
              />
            </RowFlexContainer> */}
          </RowFlexContainer>
        )
      }
      body={
        <>
          {isBreakdownViewVisible && (
            <BreakdownView
              backButtonHandler={hideBreakdownView}
              currentDate={selectedDate}
              interval={currentInterval}
              targetCurrency={targetCurrency}
            />
          )}

          {!isBreakdownViewVisible && (
            <ColumnFlexContainer>
              {!storesDetails.length && (
                <Animation
                  title={t(areaResultTranslations.noData.key, areaResultTranslations.noData.defaultValue)}
                  animation={Animations.CONFUSED}
                />
              )}

              {!!storesDetails.length && (
                <ColumnFlexContainer
                  padding={isTablet ? [4, 4, 12, 4] : [4, 8, 12, 8]}
                  data-testid="area-result-page-wrapper"
                  height="100%"
                >
                  <Typography type={isTablet ? 'h4' : 'h3'} overflow="inherit">
                    {areaName} - ({storesByArea.length}{' '}
                    {t(salesAnalysisTranslation.stores.key, salesAnalysisTranslation.stores.defaultValue)})
                  </Typography>

                  {!isMobile ? (
                    <FilterTagsWidget
                      isClearButtonVisible={isAnyFilterActive}
                      clearButtonText={t(
                        filterTranslations.clearAllButtonText.key,
                        filterTranslations.clearAllButtonText.defaultValue,
                      )}
                      clearButtonHandler={clearAllFilters}
                      filterTags={[
                        <Typography padding={[2, 0, 2, 0]} type="body3" key="results-based-on">
                          {t(
                            areaResultTranslations.resultsBasedOn.key,
                            areaResultTranslations.resultsBasedOn.defaultValue,
                          )}
                        </Typography>,
                        isDateRangeFilterActive ? (
                          <FilterTag key={dateLabel} title={dateLabel} onClick={handleDateFilterTagClick} />
                        ) : (
                          <Typography key="label" padding={[2, 0]} type="body3">
                            {dateLabel}
                          </Typography>
                        ),
                        ...selectedStores.map((store) => (
                          <FilterTag
                            key={store}
                            data-testid={`store-${store}`}
                            title={store}
                            onClick={() => handleStoreTagClick(store)}
                          />
                        )),
                      ]}
                    />
                  ) : (
                    <FilterDrawer
                      isOpen={isFilterDrawerOpen}
                      onDrawerClose={() => setIsFilterDrawerOpen(false)}
                      onClearAllFilters={clearAllFilters}
                      filtersSpecs={[
                        {
                          key: 'store',
                          type: FilterType.MultiSelectList,
                          title: 'Store',
                          options: storeMenuItems.map((item) => ({
                            key: item.key,
                            title: item.key,
                          })),
                          selectedOptionsKeys: selectedStores,
                          onSelect: (selectedItemKeys) => setSelectedStores(selectedItemKeys),
                        },
                      ]}
                    />
                  )}

                  <DynamicTab
                    tabLabelArray={[
                      {
                        tabName: t(
                          widgetCategory.salesEfficiency.key,
                          widgetCategory.salesEfficiency.defaultValue,
                        ),
                        isTabVisible: true,
                        icon: <Icon data-testid="infoIcon" icon={Icons.SALESEFFICIENCY} size="small" />,
                        iconPosition: 'start',
                        labelType: 'body3' as Typographies,
                        isTabLoading: salesEfficiencyViewLoading,
                      },
                      {
                        tabName: t(
                          widgetCategory.customerBaseValue.key,
                          widgetCategory.customerBaseValue.defaultValue,
                        ),
                        isTabVisible: true,
                        icon: <Icon data-testid="infoIcon" icon={Icons.CUSTOMERVALUE} size="small" />,
                        iconPosition: 'start',
                        labelType: 'body3' as Typographies,
                        isTabLoading: customerBaseValueViewLoading,
                      },
                      {
                        tabName: t(
                          widgetCategory.customerExperience.key,
                          widgetCategory.customerExperience.defaultValue,
                        ),
                        isTabVisible: true,
                        icon: <Icon data-testid="infoIcon" icon={Icons.PROFILE} size="small" />,
                        iconPosition: 'start',
                        labelType: 'body3' as Typographies,
                        isTabLoading: customerExperienceViewLoading,
                      },
                    ]}
                    handleTabChange={handleTabChange}
                    tabValue={tabIndex}
                    variant="scrollable"
                    tabBgColor="inherit"
                    content={[]}
                  />

                  {tabIndex === 0 && (
                    <SalesEfficiencyView
                      isLoading={salesEfficiencyViewLoading}
                      isError={salesEfficiencyViewError}
                      storesByArea={storesByArea}
                      salesEfficiencyData={salesEfficiencyViewData}
                      selectedStores={selectedStores}
                      formatCurrency={getFormattedCurrency}
                    />
                  )}
                  {tabIndex === 1 && (
                    <CustomerBaseValueView
                      isLoading={customerBaseValueViewLoading}
                      isError={customerBaseValueViewError}
                      storesByArea={storesByArea}
                      customerData={customerBaseValueViewData}
                      selectedStores={selectedStores}
                    />
                  )}
                  {tabIndex === 2 && (
                    <CustomerExperienceView
                      isLoading={customerExperienceViewLoading}
                      isError={customerExperienceViewError}
                      storesByArea={storesByArea}
                      customerExperienceData={customerExperienceViewData}
                      selectedStores={selectedStores}
                    />
                  )}
                </ColumnFlexContainer>
              )}
            </ColumnFlexContainer>
          )}
        </>
      }
    />
  );
});
